/**
 * SettingsTab Component
 *
 * Development settings panel including 2FA bypass, update banner testing,
 * and comp time warning popups.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    FiShield,
    FiRefreshCw,
    FiClock,
    FiStar,
    FiMail
} from 'react-icons/fi';

const SettingsTab = ({
    bypass2FA,
    onToggle2FA,
    onTriggerUpdateBanner,
    onShowNotification,
    onTriggerWelcomeBack,
    apiRequest
}) => {
    const [sendingEmail, setSendingEmail] = useState(false);
    return (
        <div className="dos-settings-section">
            <div className="dos-section-header">
                <FiShield className="dos-section-icon" />
                <span className="dos-section-title">Development Settings</span>
            </div>

            <div className="dos-setting-item">
                <div className="dos-setting-info">
                    <div className="dos-setting-label">Two-Factor Authentication</div>
                    <div className="dos-setting-description">
                        {bypass2FA ? 'Disabled - Login without email codes' : 'Enabled - Email codes required'}
                    </div>
                </div>
                <button
                    className={`dos-toggle-button ${bypass2FA ? 'dos-toggle-off' : 'dos-toggle-on'}`}
                    onClick={onToggle2FA}
                    title={bypass2FA ? 'Enable 2FA' : 'Disable 2FA'}
                >
                    <div className="dos-toggle-slider">
                        <div className="dos-toggle-knob"></div>
                    </div>
                    <span className="dos-toggle-text">
                        {bypass2FA ? 'OFF' : 'ON'}
                    </span>
                </button>
            </div>

            <div className="dos-setting-item">
                <div className="dos-setting-info">
                    <div className="dos-setting-label">Update Banner</div>
                    <div className="dos-setting-description">
                        Test the update notification banner
                    </div>
                </div>
                <button
                    className="dos-action-button"
                    onClick={onTriggerUpdateBanner}
                    title="Show update banner"
                >
                    <FiRefreshCw />
                    <span>Trigger Banner</span>
                </button>
            </div>

            <div className="dos-setting-item">
                <div className="dos-setting-info">
                    <div className="dos-setting-label">Comp Time Warning</div>
                    <div className="dos-setting-description">
                        Test the comp time warning login popup (80+ hours)
                    </div>
                </div>
                <button
                    className="dos-action-button"
                    onClick={() => {
                        const event = new CustomEvent('test-comptime-warning', {
                            detail: { compBalance: 85.5 }
                        });
                        window.dispatchEvent(event);
                        onShowNotification('Comp time warning popup shown!', 'success');
                    }}
                    title="Show comp time warning"
                >
                    <FiClock />
                    <span>Test Popup</span>
                </button>
            </div>

            <div className="dos-setting-item">
                <div className="dos-setting-info">
                    <div className="dos-setting-label">Welcome Back</div>
                    <div className="dos-setting-description">
                        Test the welcome back celebration animation
                    </div>
                </div>
                <button
                    className="dos-action-button"
                    onClick={() => {
                        onTriggerWelcomeBack?.();
                        onShowNotification('Welcome back animation triggered!', 'success');
                    }}
                    title="Show welcome back"
                >
                    <FiStar />
                    <span>Test Welcome</span>
                </button>
            </div>

            <div className="dos-setting-item">
                <div className="dos-setting-info">
                    <div className="dos-setting-label">Test Email</div>
                    <div className="dos-setting-description">
                        Send test email to admin@clockwisecpa.app
                    </div>
                </div>
                <button
                    className="dos-action-button"
                    onClick={async () => {
                        if (!apiRequest) {
                            onShowNotification('API request not available', 'error');
                            return;
                        }
                        setSendingEmail(true);
                        try {
                            const response = await apiRequest('/api/dev/test-email', {
                                method: 'POST',
                                body: JSON.stringify({})
                            });
                            const data = await response.json();
                            if (response.ok) {
                                onShowNotification(`Email sent! ID: ${data.resend_id}`, 'success');
                            } else {
                                onShowNotification(data.error || 'Failed to send email', 'error');
                            }
                        } catch (err) {
                            onShowNotification(`Error: ${err.message}`, 'error');
                        } finally {
                            setSendingEmail(false);
                        }
                    }}
                    title="Send test email"
                    disabled={sendingEmail}
                >
                    <FiMail />
                    <span>{sendingEmail ? 'Sending...' : 'Send Email'}</span>
                </button>
            </div>
        </div>
    );
};

SettingsTab.propTypes = {
    bypass2FA: PropTypes.bool.isRequired,
    onToggle2FA: PropTypes.func.isRequired,
    onTriggerUpdateBanner: PropTypes.func.isRequired,
    onShowNotification: PropTypes.func.isRequired,
    onTriggerWelcomeBack: PropTypes.func,
    apiRequest: PropTypes.func
};

export default SettingsTab;
